import { inject, Component } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { BusinessModel, BusinessTypeModel, CreateBusinessRequest, UserSummary } from '../../../models/user.model';
import { Role, Permission } from '../../../models/access.model';
import { Country } from '../../../models/geography.model';
import { UserRoleService } from '../../../services/user-role.service';
import { BusinessService } from '../../../services/business.service';
import { GeographyService } from '../../../services/geography.service';
import { AuthService } from '../../../services/auth/auth.service';
import { PlatformSettingsService } from '../../../services/platform-settings.service';
import { MaintenanceService } from '../../../services/maintenance.service';
import { TestBusiness } from '../../../models/platform-settings.model';
import { HttpErrorResponse } from '@angular/common/http';
import { accountRoleLabel, hasAnyRole, OWNER_ROLES } from '../../../shared/roles.constants';
import { firstValidationError } from '../../../shared/http-errors';

@Component({
  selector: 'app-businesses-page',
  standalone: false,
  templateUrl: './businesses-page.component.html',
  styleUrls: ['./businesses-page.component.css']
})
export class BusinessesPageComponent {
  readonly staffAuth = inject(AuthService);
  isLoading = false;
  allBusinesses: BusinessModel[] = [];
  filteredBusinesses: BusinessModel[] = [];
  searchTerm = '';
  selectedBusiness: BusinessModel | null = null;

  // New business (admin)
  get canCreateBusiness(): boolean { return this.authService.can('Businesses.Create'); }
  showCreateForm = false;
  createSaving = false;
  createError = '';
  createMessage = '';
  /** The business was created but its owner link failed, so the banner is a warning. */
  createMessageIsWarning = false;
  /** The selected business's profile is in edit mode (this admin holds its edit lease). */
  businessEditing = false;
  /** Business the createMessage banner refers to. */
  createdBusinessId: string | null = null;
  createForm: FormGroup;
  users: UserSummary[] = [];
  businessTypes: BusinessTypeModel[] = [];
  countries: Country[] = [];
  createLookupsLoading = false;
  private createLookupsLoaded = false;

  // Platform test business: the one business platform tooling exercises (report
  // self-tests today). Superadmin-only, matching the API — Support can see businesses
  // but must not repoint platform tooling at one.
  get canSetTestBusiness(): boolean { return this.authService.can('Platform.Settings.Manage'); }
  testBusiness: TestBusiness | null = null;
  testBusinessSaving = false;
  testBusinessError = '';

  constructor(
    private userRoleService: UserRoleService,
    private businessService: BusinessService,
    private geographyService: GeographyService,
    private authService: AuthService,
    private platformSettingsService: PlatformSettingsService,
    private maintenanceService: MaintenanceService,
    private formBuilder: FormBuilder,
    private router: Router,
    private location: Location
  ) {
    if (this.canSetTestBusiness) {
      this.loadTestBusiness();
    }
    this.createForm = this.formBuilder.group({
      ownerUserId: this.formBuilder.control<string | null>(null, [Validators.required]),
      name: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(100)] }),
      businessTypeId: this.formBuilder.control<string | null>(null, [Validators.required]),
      countryId: this.formBuilder.control<number | null>(null, [Validators.required]),
      address: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      city: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      state: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
      zip: this.formBuilder.control<string>('', { nonNullable: true }),
      customerServicePhone: this.formBuilder.control<string>('', { nonNullable: true })
    });
  }

  ngOnInit() {
    this.loadBusinesses();
    this.loadMaintenance();
  }

  /** Businesses under maintenance (specs/maintenance), for the pill in the list. SuperAdmin-only on the API; others see no pill. */
  private readonly maintenanceIds = new Set<string>();

  private loadMaintenance(): void {
    if (!this.staffAuth.can('Platform.Maintenance.Manage')) return;
    this.maintenanceService.overview().subscribe({
      next: overview => {
        this.maintenanceIds.clear();
        (overview?.businesses || []).forEach(b => this.maintenanceIds.add(String(b.businessId).toLowerCase()));
      },
      error: () => { /* the pill is a convenience; the profile's Access panel says why it could not load */ }
    });
  }

  isUnderMaintenance(b: BusinessModel | null): boolean {
    return !!b && this.maintenanceIds.has(String(b.businessId).toLowerCase());
  }

  private loadBusinesses() {
    this.isLoading = true;
    this.businessService.getBusinesses().subscribe(biz => {
      this.allBusinesses = biz || [];
      this.filteredBusinesses = [...this.allBusinesses];
      this.onSearch(this.searchTerm);
    }).add(() => this.isLoading = false);
  }

  onSearch(term: string) {
    const q = (term || '').trim().toLowerCase();
    this.searchTerm = term;
    if (!q) {
      this.filteredBusinesses = [...this.allBusinesses];
      return;
    }
    this.filteredBusinesses = (this.allBusinesses || []).filter(b => {
      const name = (b.name || '').toLowerCase();
      const id = String(b.businessId || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }

  onSelectBusiness(b: BusinessModel) {
    // The "created" banner belongs to the business that was just created, so it
    // survives the auto-select that follows creation but not a manual switch.
    if (String(b.businessId) !== String(this.createdBusinessId)) {
      this.createMessage = '';
      this.createdBusinessId = null;
    }
    this.selectedBusiness = b;
    // A different business opens view-only; its profile's edit bar reports when that changes.
    this.businessEditing = false;
    this.showCreateForm = false;
    const id = String(b.businessId);
    // Update URL without navigating away from the page
    this.location.replaceState(`/businesses/${id}`);
  }

  startCreateBusiness(): void {
    if (!this.staffAuth.can('Businesses.Create')) return;
    this.showCreateForm = true;
    this.selectedBusiness = null;
    this.createError = '';
    this.createMessage = '';
    this.createdBusinessId = null;
    this.createForm.reset({
      ownerUserId: null,
      name: '',
      businessTypeId: null,
      countryId: null,
      address: '',
      city: '',
      state: '',
      zip: '',
      customerServicePhone: ''
    });
    this.loadCreateLookups();
  }

  private userName(u: UserSummary): string {
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '';
  }

  /** Name, kind of account and email, so a seller is easy to tell from a Plus owner or a customer. */
  ownerOptionLabel(u: UserSummary): string {
    const kind = accountRoleLabel((u.roles || []).map(r => r.roleName).filter(n => !!n));
    return `${this.userName(u)} · ${kind} (${u.email})`;
  }

  /** True once the user has touched (or submitted) a required field they left empty. */
  invalid(controlName: string): boolean {
    const control = this.createForm.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  cancelCreateBusiness(): void {
    this.showCreateForm = false;
    this.createSaving = false;
    this.createError = '';
  }

  /**
   * Owner, business type and country are all required, so the form is unusable
   * unless every lookup arrives. Latch the "loaded" flag only on success, so a
   * failed load can be retried by reopening the form rather than leaving the
   * dropdowns permanently empty.
   */
  private loadCreateLookups(): void {
    if (this.createLookupsLoaded || this.createLookupsLoading) {
      return;
    }
    this.createLookupsLoading = true;

    forkJoin({
      users: this.userRoleService.getUsers(),
      types: this.businessService.getBusinessTypes(),
      countries: this.geographyService.getCountries()
    }).subscribe({
      next: ({ users, types, countries }) => {
        // Only an account holder (AccountHolder / MarketplaceAccountHolder) can own a business:
        // the business is billed under the owner's plan, and the plan comes with that role. The API
        // refuses anyone else, so nobody else is offered.
        this.users = (users || [])
          .filter(u => !u.isDisabled && hasAnyRole((u.roles || []).map(r => r.roleName), OWNER_ROLES))
          .sort((a, b) => this.userName(a).localeCompare(this.userName(b)));
        this.businessTypes = types || [];
        this.countries = countries || [];
        this.createLookupsLoaded = true;
      },
      error: () => {
        this.createError = 'Could not load owners, business types and countries. Close the form and try again.';
      }
    }).add(() => (this.createLookupsLoading = false));
  }

  submitCreateBusiness(): void {
    if (!this.staffAuth.can('Businesses.Create')) return;
    if (this.createSaving || !this.canCreateBusiness) {
      return;
    }

    this.createForm.markAllAsTouched();
    if (this.createForm.invalid) {
      // Without this the button click is a no-op and the form looks broken.
      // Don't mask a lookup failure — empty dropdowns aren't the user's fault.
      if (this.createLookupsLoaded) {
        this.createError = 'Please fill in every required field.';
      }
      return;
    }

    const value = this.createForm.getRawValue();
    const request: CreateBusinessRequest = {
      ownerUserId: value.ownerUserId,
      name: (value.name || '').trim(),
      businessTypeId: value.businessTypeId,
      countryId: value.countryId,
      address: (value.address || '').trim(),
      city: (value.city || '').trim(),
      state: (value.state || '').trim() || null,
      zip: (value.zip || '').trim() || null,
      customerServicePhone: (value.customerServicePhone || '').trim() || null
    };

    this.createSaving = true;
    this.createError = '';
    this.createMessage = '';

    this.businessService.createBusiness(request).subscribe({
      next: (response) => {
        this.showCreateForm = false;
        this.createdBusinessId = response?.businessId ?? null;
        const owner = this.users.find(u => u.userId === request.ownerUserId);
        // The warning already says the business was created.
        this.createMessageIsWarning = !!response?.warning;
        this.createMessage = response?.warning
          || `Business "${request.name}" created, owned by ${owner ? this.userName(owner) : 'the selected user'}.`;
        this.reloadAndSelect(response?.businessId);
      },
      error: (error: HttpErrorResponse) => {
        this.createError = error?.error?.message
          || firstValidationError(error)
          || 'Failed to create business.';
      }
    }).add(() => (this.createSaving = false));
  }

  private reloadAndSelect(businessId: string | undefined): void {
    this.isLoading = true;
    this.businessService.getBusinesses().subscribe(biz => {
      this.allBusinesses = biz || [];
      this.onSearch(this.searchTerm);
      if (businessId) {
        const created = this.allBusinesses.find(b => String(b.businessId) === String(businessId));
        if (created) {
          this.onSelectBusiness(created);
        }
      }
    }).add(() => this.isLoading = false);
  }

  // ── Platform test business ────────────────────────────────────────────────

  private loadTestBusiness(): void {
    this.platformSettingsService.getDefaultTestBusiness().subscribe({
      next: business => this.testBusiness = business,
      error: err => console.error(err)
    });
  }

  isTestBusiness(b: BusinessModel | null): boolean {
    if (!b || !this.testBusiness) return false;
    // Business ids come back as strings from one endpoint and as raw values from
    // another; compare as strings so the badge does not silently miss.
    return String(b.businessId).toLowerCase() === String(this.testBusiness.businessId).toLowerCase();
  }

  setAsTestBusiness(b: BusinessModel): void {
    if (!this.staffAuth.can('Platform.Settings.Manage')) return;
    this.testBusinessSaving = true;
    this.testBusinessError = '';
    this.platformSettingsService.setDefaultTestBusiness(String(b.businessId)).subscribe({
      next: result => {
        this.testBusiness = result;
        this.testBusinessSaving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.testBusinessSaving = false;
        this.testBusinessError = typeof err.error === 'string' && err.error
          ? err.error
          : 'Could not set the platform test business.';
        console.error(err);
      }
    });
  }

  clearTestBusiness(): void {
    if (!this.staffAuth.can('Platform.Settings.Manage')) return;
    this.testBusinessSaving = true;
    this.testBusinessError = '';
    this.platformSettingsService.clearDefaultTestBusiness().subscribe({
      next: () => {
        this.testBusiness = null;
        this.testBusinessSaving = false;
      },
      error: (err: HttpErrorResponse) => {
        this.testBusinessSaving = false;
        this.testBusinessError = 'Could not clear the platform test business.';
        console.error(err);
      }
    });
  }

  needsAttention(b: BusinessModel): boolean {
    if (!b) return false;
    const inactive = b.isActive === false;
    // const missingCoreFeature = Array.isArray(b.features)
    //   ? b.features.some(f => f.isArea && f.included === false)
    //   : false;

    // Business "category" – use taxCategory as proxy if no explicit category exists
    const missingCategory = !b.businessCategory || !String(b.businessCategory).trim();

    // Address check: missing street OR insufficient location details
    const missingAddress = !b.address || !String(b.address).trim()
      || (!b.city && !b.state && !b.zip);

    // Phone check: if all known numbers are missing/blank
    const noPhones = [b.phone]
      .every(p => !p || !String(p).trim());

    // User count check: business should have at least one user
    const userCount = (b as any).userCount as number | undefined;
    const noUsers = (userCount ?? 0) <= 0;

    // No owner: the business is billed under its owner's plan, so without one it has no plan,
    // no entitlements and nobody who can add a terminal.
    const noOwner = !b.ownerUserId;

    return inactive /*|| missingCoreFeature*/ || missingCategory || missingAddress || noPhones || noUsers || noOwner;
  }

  // Details moved to BusinessProfileComponent
}
